import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, TableRow, TableCell, IconButton } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useNavigate, useParams } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/store';
import { fetchDrawing, updateDrawing } from 'store/actions/drawingActions';
import { WorkflowStatusId } from 'types/enums/WorkflowStatus';
import { CustomTextField } from 'components/FormFields/CustomTextField/CustomTextField';
import { CustomDatePicker } from 'components/FormFields/CustomDatePicker/CustomDatePicker';
import RedButton from 'Styled/RedButton';
import TextButton from 'Styled/TextButton';
import { isFulfilled } from '@reduxjs/toolkit';
import { PageHeader } from 'components/Layout/PageHeader';
import { StyledTable } from 'components/StyledTable/StyledTable';
import { useTableSort, type SortableColumn } from 'components/StyledTable/useTableSort';
import { apiAddDrawingPrize, apiDeleteDrawingPrize, apiGetDrawingPrizes, apiGetPrizes, apiUpdateDrawingPrizes } from 'API/lotteryApi';
import type { IDrawingPrize } from 'types/IDrawingPrize';
import type { IPrize } from 'types/IPrize';
import { CustomSelect } from 'components/FormFields/CustomSelect/CustomSelect';
import { CustomMenu, type CustomMenuRow } from 'components/CustomMenu/CustomMenu';
import type { ApiError } from 'API/apiClient';
import { prizeLabel } from 'utility/prizeLabel';
import { canEditDrawing } from 'utility/drawingEditable';
import { preventEnterSubmit } from 'utility/preventEnterSubmit';

const validationSchema = Yup.object({
  drawingDate: Yup.string().required('Ziehungsdatum ist erforderlich'),
});

const drawnPrizeColumns: SortableColumn<IDrawingPrize>[] = [
  { name: 'Gewinn', width: '50%', sortBy: (row) => prizeLabel(row.typ, row.value, row.title) },
  { name: 'Endziffer(n)', width: '50%', sortBy: (row) => row.digits },
  { name: 'Aktionen', width: '1%' },
];

const insertDrawnPrize = (rows: IDrawingPrize[], added: IDrawingPrize, rankOf: (prizeId: number) => number): IDrawingPrize[] => {
  const lastSame = rows.map((row) => row.prizeId).lastIndexOf(added.prizeId);
  const at = lastSame >= 0 ? lastSame + 1 : rows.findIndex((row) => rankOf(row.prizeId) > rankOf(added.prizeId));

  return at < 0 ? [...rows, added] : [...rows.slice(0, at), added, ...rows.slice(at)];
};

export const DrawingEditPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { lotteryId, drawingId } = useParams<{ lotteryId: string; drawingId: string }>();
  const { selectedDrawing } = useAppSelector((state) => state.drawings);
  const [drawnPrizes, setDrawnPrizes] = useState<IDrawingPrize[]>([]);
  const [basePrizes, setBasePrizes] = useState<IPrize[]>([]);
  const [prizeToAdd, setPrizeToAdd] = useState('');
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuRow, setMenuRow] = useState<IDrawingPrize | null>(null);
  const { rows: sortedDrawnPrizes, columns: drawnPrizeTableColumns, sort: drawnPrizeSort } = useTableSort(drawnPrizeColumns, drawnPrizes);
  const [pageError, setPageError] = useState<string | null>(null);

  useEffect(() => {
    if (drawingId) {
      dispatch(fetchDrawing(Number(drawingId)));
    }
  }, [dispatch, drawingId]);

  useEffect(() => {
    if (!drawingId) return;

    // The drawing being edited can change while the answer is on its way.
    let current = true;
    apiGetDrawingPrizes(Number(drawingId))
      .then((rows) => {
        if (current) setDrawnPrizes(rows);
      })
      .catch((error: ApiError) => {
        if (current) setPageError(error.message);
      });

    return () => {
      current = false;
    };
  }, [drawingId]);

  const drawingBaseId = selectedDrawing?.drawingBaseId;

  useEffect(() => {
    if (!drawingBaseId) return;

    let current = true;
    apiGetPrizes(drawingBaseId)
      .then((rows) => {
        if (current) setBasePrizes(rows);
      })
      .catch((error: ApiError) => {
        if (current) setPageError(error.message);
      });

    return () => {
      current = false;
    };
  }, [drawingBaseId]);

  const prizeRank = (prizeId: number) => {
    const index = basePrizes.findIndex((prize) => prize.id === prizeId);
    return index < 0 ? Number.MAX_SAFE_INTEGER : index;
  };

  const addDraw = async (prizeId: number) => {
    if (!drawingId) return;
    setPageError(null);
    try {
      const created = await apiAddDrawingPrize(Number(drawingId), prizeId);
      setDrawnPrizes((rows) => insertDrawnPrize(rows, created, prizeRank));
      setPrizeToAdd('');
    } catch (error) {
      setPageError((error as ApiError).message);
    }
  };

  const copyDraw = async (row: IDrawingPrize) => {
    if (!drawingId) return;
    setPageError(null);
    try {
      const created = await apiAddDrawingPrize(Number(drawingId), row.prizeId, row.digits ?? undefined);
      setDrawnPrizes((rows) => {
        const index = rows.findIndex((entry) => entry.id === row.id);
        return [...rows.slice(0, index + 1), created, ...rows.slice(index + 1)];
      });
    } catch (error) {
      setPageError((error as ApiError).message);
    }
  };

  const removeDraw = async (drawingPrizeId: number) => {
    if (!drawingId) return;
    setPageError(null);
    try {
      await apiDeleteDrawingPrize(Number(drawingId), drawingPrizeId);
      setDrawnPrizes((rows) => rows.filter((row) => row.id !== drawingPrizeId));
    } catch (error) {
      setPageError((error as ApiError).message);
    }
  };

  const setDigits = (id: number, digits: string) => setDrawnPrizes((rows) => rows.map((row) => (row.id === id ? { ...row, digits } : row)));

  const buildMenuItems = (): CustomMenuRow[] =>
    menuRow
      ? [
          { name: 'Gewinn hinzufügen', onClick: () => void copyDraw(menuRow) },
          { name: 'Gewinn entfernen', onClick: () => void removeDraw(menuRow.id) },
        ]
      : [];

  const closeMenu = () => {
    setMenuAnchor(null);
    setMenuRow(null);
  };

  if (!selectedDrawing) return null;

  const workflowStatus = selectedDrawing.workflowDrawings?.[0]?.workflowStatusId;

  if (!canEditDrawing(workflowStatus)) {
    const lockReason =
      workflowStatus === WorkflowStatusId.FREIGEGEBEN
        ? 'da sie freigegeben wurde'
        : workflowStatus === WorkflowStatusId.ZUR_FREIGABE_VORGELEGT
          ? 'da sie zur Freigabe vorgelegt wurde'
          : 'da ihr Workflow-Status dies nicht zulässt';

    return (
      <Box>
        <PageHeader title="Ziehung editieren" />
        <Alert severity="warning" sx={{ marginBottom: 2 }}>
          Diese Ziehung kann nicht bearbeitet werden, {lockReason}.
        </Alert>
        <TextButton onClick={() => navigate(`/lottery/${lotteryId}/drawings`)}>Zurück zur Übersicht</TextButton>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader title={ `Ziehungsbasis: ${selectedDrawing.drawingBase?.name || '-'}` } />

      <Formik
        enableReinitialize
        initialValues={{
          drawingDate: selectedDrawing.drawingDate ? String(selectedDrawing.drawingDate).substring(0, 10) : '',
          drawingDateNext: selectedDrawing.drawingDateNext ? String(selectedDrawing.drawingDateNext).substring(0, 10) : '',
          datePublish: selectedDrawing.datePublish ? String(selectedDrawing.datePublish).substring(0, 10) : '',
        }}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          setPageError(null);
          const result = await dispatch(
            updateDrawing({
              id: Number(drawingId),
              data: {
                drawingDate: values.drawingDate || undefined,
                drawingDateNext: values.drawingDateNext || undefined,
                datePublish: values.datePublish || undefined,
              },
            })
          );
          if (!isFulfilled(result)) return;

          try {
            if (drawnPrizes.length > 0) {
              await apiUpdateDrawingPrizes(
                Number(drawingId),
                drawnPrizes.map((row) => ({
                  id: row.id,
                  digits: row.digits ?? '',
                  dateWinningNotification: row.dateWinningNotification,
                }))
              );
            }

          } catch (error) {
            setPageError((error as ApiError).message);
            return;
          }

          navigate(`/lottery/${lotteryId}/drawings`);
        }}
      >
        {({ values, errors, touched, setFieldValue, isSubmitting }) => (
          <Form onKeyDown={preventEnterSubmit}>
            <Box sx={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ maxWidth: 600, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <CustomDatePicker
                  label="Datum der aktuellen Ziehung"
                  value={values.drawingDate}
                  onChange={(val) => setFieldValue('drawingDate', val)}
                  error={touched.drawingDate && Boolean(errors.drawingDate)}
                  helperText={touched.drawingDate ? (errors.drawingDate as string) : ''}
                />

                <CustomDatePicker label="Datum der nächsten Ziehung" value={values.drawingDateNext} onChange={(val) => setFieldValue('drawingDateNext', val)} />

                <CustomDatePicker label="Veröffentlichung der nächsten Ziehung" value={values.datePublish} onChange={(val) => setFieldValue('datePublish', val)} />
              </Box>

              {pageError && <Alert severity="error">{pageError}</Alert>}

              {drawnPrizes.length === 0 && !pageError && (
                <Typography variant="body1" sx={{ color: '#969696' }}>
                  Dieser Ziehung sind keine Gewinne zugeordnet.
                </Typography>
              )}

              {basePrizes.length > 0 && (
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', maxWidth: 600 }}>
                  <CustomSelect
                    name="prize-to-add"
                    label="Gewinn hinzufügen"
                    value={prizeToAdd}
                    onChange={setPrizeToAdd}
                    options={basePrizes.map((prize) => ({ id: prize.id, title: prizeLabel(prize.typ, prize.value, prize.title) }))}
                  />
                  <RedButton type="button" disabled={!prizeToAdd} onClick={() => void addDraw(Number(prizeToAdd))}>
                    Hinzufügen
                  </RedButton>
                </Box>
              )}

              {drawnPrizes.length > 0 && (
                <StyledTable columns={drawnPrizeTableColumns} sort={drawnPrizeSort}>
                  {sortedDrawnPrizes.map((row, index) => (
                    <TableRow key={row.id} className={`psf-styled-table__body-row ${index === sortedDrawnPrizes.length - 1 ? 'psf-styled-table__body-last-row' : ''}`}>
                      <TableCell>{prizeLabel(row.typ, row.value, row.title)}</TableCell>
                      <TableCell>
                        <CustomTextField
                          name={`digits-${row.id}`}
                          value={row.digits ?? ''}
                          // Final digits are digits only, as in the old form.
                          onChange={(event) => setDigits(row.id, event.target.value.replace(/[^0-9]/g, ''))}
                        />
                      </TableCell>
                      <TableCell>
                        <Box className="psf-styled-table__body-cell-action-container">
                          <IconButton
                            className="psf-styled-table__body-cell-action-button"
                            aria-label="Aktionen"
                            onClick={(event) => {
                              setMenuAnchor(event.currentTarget);
                              setMenuRow(row);
                            }}
                          >
                            <MoreVertIcon className="psf-styled-table__body-cell-action-button-icon" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </StyledTable>
              )}

              <CustomMenu anchorEl={menuAnchor} handleMenuClose={closeMenu} withDivider isArrowRight items={buildMenuItems()} />

              <Box sx={{ display: 'flex', gap: 2, marginTop: 2 }}>
                <RedButton type="submit" disabled={isSubmitting}>
                  Speichern
                </RedButton>
                <TextButton onClick={() => navigate(`/lottery/${lotteryId}/drawings`)}>Abbrechen</TextButton>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};
